import React, { useState, useEffect, useMemo, useContext, useRef, memo } from "react";
import { ManagedWindow, closeWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { JPBManager } from "./JPBManager";
import type { JPBDateBounds } from "./JPBManager";
import { JPBReferenceProperty, AreaGrouping, YearGrouping, DEFAULT_ASSUMPTIONS, AreaUnit, PriceMode, JPBReportData, JPBReportSelectedLayer, JPBAssumptions, AverageCalculationMethod, JPBPriceIndexSource } from "./JPBState";
import { JPBHistogram } from "./JPBHistogram";
import { JPBTable, JPBTableRef } from "./JPBTable";
import { JPBSearchArea } from "./JPBSearchArea";
import { JPBStatistics } from "./JPBStatistics";
import { JPBPolygonAnalyzer } from "./JPBPolygonAnalyzer";
import type { JPBPriceIndexSourceWindowParams } from "./JPBPriceIndexSourceWindow";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { getCheckedItemsFromCurrentAssumptions } from "./JPBFunc";
import { MapitStateContext, actionSetMapInteractionState, MapInteractionState, type LayersType } from "src/states/MapitState";
import type { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import type { GeoJSONSource } from "maplibre-gl";
import { feature } from "@turf/helpers";
import { bboxPolygon, difference, featureCollection, buffer } from "@turf/turf";
import "../../css/propertyInfoStyle.css";
import { LayerVariabilityType, LayerType } from "src/common/managers/Types";
import { setupJPBMapLayers, removeJPBMapLayers, updateFeatureStates, SOURCE_ID, SOURCE_ID_POLY, LAYER_ID, LAYER_ID_ISSUE, LAYER_ID_BUILDING, LAYER_ID_POLY, LAYER_ID_FARMLAND, LAYER_ID_JORDBUNDSKORT, SOURCE_ID_JORDBUNDSKORT, highlightPolygonOnMap, createJordbundskortGeoJson } from "./JPBMapLayers";
import { Spinner } from "react-bootstrap";
import { handleGenerateJPBRapport } from "./JPBRepportGen";
import { renderJPBOverviewImage, renderJPBHeatmapImage, renderJPBOverviewWithJordbundImage, renderJPBHistogramImage } from "./JPBMapSnapshot";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ActionTracker } from "src/common/managers/ActionTracker";

const MASK_SOURCE_ID = 'jpb-search-area-mask-source';
const MASK_LAYER_ID = 'jpb-search-area-mask';

function toStartOfDay(date: Date): Date {
   const d = new Date(date);
   d.setHours(0, 0, 0, 0);
   return d;
}

function withYearsOffset(date: Date, years: number): Date {
   const d = new Date(date);
   d.setFullYear(d.getFullYear() + years);
   return d;
}

function stableStringify(value: unknown): string {
   const normalize = (input: unknown): unknown => {
      if (Array.isArray(input)) {
         return input.map(normalize);
      }
      if (input && typeof input === "object") {
         const entries = Object.entries(input as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
         return entries.reduce<Record<string, unknown>>((acc, [key, val]) => {
            acc[key] = normalize(val);
            return acc;
         }, {});
      }
      return input;
   };
   return JSON.stringify(normalize(value));
}

type MapitStateDispatch = React.Dispatch<any>;

type MapInfoRef = React.MutableRefObject<{ mapStyle?: any } | null>;

function JPBWindowInner(props: {
   managedWindow: ManagedWindow;
   map: MapFacadeMaplibre;
   layers: LayersType;
   dispatch: MapitStateDispatch;
   mapInfoRef: MapInfoRef;
}) {
   const { managedWindow, map: mapFacade, layers, dispatch: mapitStateDispatch, mapInfoRef } = props;
   const { dispatch: windowDispatch } = useWindowContext();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const hasDebugAccess = hasAccessToFeature(Feature.Debugging);
   const hasAgroCuraIndexAccess = hasAccessToFeature(Feature.JPBAgroCuraIndexAllow);
   const hasMolbakIndexAccess = hasAccessToFeature(Feature.JPBMolbakIndexAllow);

   const getDefaultAccessiblePriceIndexSource = (): JPBPriceIndexSource => {
      if (hasMolbakIndexAccess) return JPBPriceIndexSource.Molbak;
      if (hasAgroCuraIndexAccess) return JPBPriceIndexSource.AgroCura;
      return JPBPriceIndexSource.None;
   };

   const normalizeAccessiblePriceIndexSource = (
      source: string | JPBPriceIndexSource | null | undefined,
      fallbackToDefaultWhenNone = false
   ): JPBPriceIndexSource => {
      const normalized = JPBManager.normalizePriceIndexSource(source);
      if (normalized === JPBPriceIndexSource.None) {
         return fallbackToDefaultWhenNone ? getDefaultAccessiblePriceIndexSource() : JPBPriceIndexSource.None;
      }
      if (normalized === JPBPriceIndexSource.AgroCura && !hasAgroCuraIndexAccess) return getDefaultAccessiblePriceIndexSource();
      if (normalized === JPBPriceIndexSource.Molbak && !hasMolbakIndexAccess) return getDefaultAccessiblePriceIndexSource();
      return normalized;
   };

   // MARK: State
   const [referenceProperties, setReferenceProperties] = useState<JPBReferenceProperty[]>([]);
   const [loading, setLoading] = useState<boolean>(false);
   const [error, setError] = useState<string | null>(null);
   const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
   const [bufferGeometry, setBufferGeometry] = useState<any>(null);
   const [areaGrouping, setAreaGrouping] = useState<AreaGrouping>(AreaGrouping.All);
   const [hideUnselected, setHideUnselected] = useState<'none' | 'unchecked' | 'locked'>('none');
   const [customPriceAssumptions, setCustomPriceAssumptions] = useState<{ pricePerMeterMin?: number, pricePerMeterMax?: number, excludeBuildings?: boolean } | undefined>(undefined);
   const [systemPriceAssumptions, setSystemPriceAssumptions] = useState<{ pricePerMeterMin: number, pricePerMeterMax: number } | undefined>(undefined);
   const [selectedLayer, setSelectedLayer] = useState<number | undefined>(undefined);
   const [areaUnit, setAreaUnit] = useState<AreaUnit>(() => {
      const saved = localStorage.getItem("jpb.areaUnit");
      return (saved as AreaUnit) || AreaUnit.SquareMeter;
   });
   const [priceMode, setPriceMode] = useState<PriceMode>(() => {
      const saved = localStorage.getItem("jpb.priceMode");
      return (saved as PriceMode) || PriceMode.Standard;
   });
   const [averageCalculationMethod, setAverageCalculationMethod] = useState<AverageCalculationMethod>(() => {
      const saved = localStorage.getItem("jpb.averageCalculationMethod");
      return (saved as AverageCalculationMethod) || AverageCalculationMethod.WeightedByArea;
   });
   const [priceIndexSource, setPriceIndexSource] = useState<JPBPriceIndexSource>(() => {
      const saved = localStorage.getItem("jpb.priceIndexSource");
      return normalizeAccessiblePriceIndexSource(saved, true);
   });
   const [isWindowMinimized, setIsWindowMinimized] = useState<boolean>(false);
   const [dateBounds, setDateBounds] = useState<JPBDateBounds | null>(null);
   const [appliedYearGrouping, setAppliedYearGrouping] = useState<YearGrouping>(YearGrouping.ThreeYears);
   const [appliedCalculationDateTo, setAppliedCalculationDateTo] = useState<Date | null>(null);
   const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
   const [settingsRefreshToken, setSettingsRefreshToken] = useState<number>(0);
   const [mapRefreshToken, setMapRefreshToken] = useState<number>(0);
   const tableRef = useRef<JPBTableRef>(null);
   const tableScrollAnchorRef = useRef<HTMLDivElement>(null);
   const histogramRef = useRef<HTMLDivElement>(null);
   const modalBodyRef = useRef<HTMLDivElement>(null);
   const checkedItemsRef = useRef<Set<string>>(checkedItems);
   const originalIconImagesRef = useRef<{ [layerId: string]: any }>({}); // Store original icon images
   const highlightCleanupRef = useRef<(() => void) | null>(null); // Store cleanup function for polygon highlight
   const mapRefreshTimeoutRef = useRef<number | null>(null);
   checkedItemsRef.current = checkedItems;

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(
         isWindowMinimized ? MapInteractionState.Normal : MapInteractionState.Override
      ));
   }, [isWindowMinimized, mapitStateDispatch]);

   useEffect(() => {
      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
         // Clean up any active highlight when component unmounts
         if (highlightCleanupRef.current) {
            highlightCleanupRef.current();
            highlightCleanupRef.current = null;
         }
      };
   }, [mapitStateDispatch]);

   useEffect(() => {
      const createSettingsSnapshot = () => stableStringify({
         assumptions: SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS),
         defaultRadiusKm: SettingsManager.getSystemSetting("jpb.defaultRadiusKm", 10),
         maxProperties: SettingsManager.getSystemSetting("jpb.maxProperties", 4000),
         indexName: SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5")
      });

      let previousSnapshot = createSettingsSnapshot();
      const id = window.setInterval(() => {
         const nextSnapshot = createSettingsSnapshot();
         if (nextSnapshot !== previousSnapshot) {
            previousSnapshot = nextSnapshot;
            setSettingsRefreshToken((v) => v + 1);
         }
      }, 1500);

      return () => window.clearInterval(id);
   }, []);

   useEffect(() => {
      return () => {
         if (mapRefreshTimeoutRef.current) {
            window.clearTimeout(mapRefreshTimeoutRef.current);
            mapRefreshTimeoutRef.current = null;
         }
      };
   }, []);

   useEffect(() => {
      let cancelled = false;
      (async () => {
         const preparedState = await JPBManager.prepareIndexedLandsdelState(true).catch(() => null);
         if (cancelled || !preparedState) return;
      })();

      return () => {
         cancelled = true;
      };
   }, [settingsRefreshToken]);

   useEffect(() => {
      if (!mapFacade || referenceProperties.length === 0) {
         if (mapRefreshTimeoutRef.current) {
            window.clearTimeout(mapRefreshTimeoutRef.current);
            mapRefreshTimeoutRef.current = null;
         }
         return;
      }

      if (mapRefreshTimeoutRef.current) {
         window.clearTimeout(mapRefreshTimeoutRef.current);
      }
      mapRefreshTimeoutRef.current = window.setTimeout(() => {
         setMapRefreshToken((v) => v + 1);
         mapRefreshTimeoutRef.current = null;
      }, 2000);
   }, [settingsRefreshToken, mapFacade, referenceProperties.length]);

   useEffect(() => {
      let cancelled = false;
      (async () => {
         const [bounds, metadata] = await Promise.all([
            JPBManager.fetchDateBounds().catch(() => null),
            JPBManager.fetchDatabaseMetadata().catch(() => null)
         ]);
         if (cancelled) return;

         const boundedMax = bounds?.maxDate
            ? toStartOfDay(bounds.maxDate)
            : null;
         const metadataDate = metadata?.uploadedAt
            ? toStartOfDay(new Date(metadata.uploadedAt))
            : null;

         const maxDateCandidate = boundedMax
            ? (metadataDate && metadataDate < boundedMax ? metadataDate : boundedMax)
            : (metadataDate ?? toStartOfDay(new Date()));

         const minDateCandidate = bounds?.minDate
            ? toStartOfDay(bounds.minDate)
            : withYearsOffset(maxDateCandidate, -5);

         const normalizedBounds: JPBDateBounds = {
            minDate: minDateCandidate,
            maxDate: maxDateCandidate
         };
         setDateBounds((prev) => {
            if (
               prev &&
               prev.minDate.getTime() === normalizedBounds.minDate.getTime() &&
               prev.maxDate.getTime() === normalizedBounds.maxDate.getTime()
            ) {
               return prev;
            }
            return normalizedBounds;
         });
         setAppliedCalculationDateTo(prev => {
            if (!prev) {
               return maxDateCandidate;
            }
            if (prev < minDateCandidate) return minDateCandidate;
            if (prev > maxDateCandidate) return maxDateCandidate;
            return prev;
         });
      })();

      return () => {
         cancelled = true;
      };
   }, [settingsRefreshToken]);

   useEffect(() => {
      if (selectedLayer === undefined) return
      ActionTracker.track("JPBModule", "Action", "SelectLayerForCalculation");
   }, [selectedLayer]);

   useEffect(() => {
      const normalized = normalizeAccessiblePriceIndexSource(priceIndexSource);
      if (normalized !== priceIndexSource) {
         setPriceIndexSource(normalized);
         localStorage.setItem("jpb.priceIndexSource", normalized);
      }
   }, [hasAgroCuraIndexAccess, hasMolbakIndexAccess, priceIndexSource]);

   // MARK: Handlers
   const lockedDocumentIds = useMemo(() => {
      const locked = new Set<string>();
      for (const prop of referenceProperties) {
         if (JPBManager.getSelectionLockState(prop, customPriceAssumptions).isLocked) {
            locked.add(prop.documentID);
         }
      }
      return locked;
   }, [referenceProperties, customPriceAssumptions, settingsRefreshToken]);

   const sanitizeCheckedItems = (items: Set<string>) => {
      if (lockedDocumentIds.size === 0) return items;
      const sanitized = new Set(items);
      lockedDocumentIds.forEach((id) => sanitized.delete(id));
      return sanitized;
   };

   const updateCheckedItems = (newCheckedItems: Set<string>) => {
      const sanitized = sanitizeCheckedItems(newCheckedItems);
      setCheckedItems(sanitized);

      const map = mapFacade?.getMapPop();
      if (map && map.getSource(SOURCE_ID)) {
         referenceProperties.forEach(prop => {
            map.setFeatureState(
               { source: SOURCE_ID, id: prop.documentID },
               { checked: sanitized.has(prop.documentID) }
            );
            map.setFeatureState(
               { source: SOURCE_ID_POLY, id: prop.documentID },
               { checked: sanitized.has(prop.documentID) }
            );
         });
      }
   };

   const handleRowClick = (property: JPBReferenceProperty) => {
      if (!mapFacade) {
         console.warn('handleRowClick: mapFacade is not available');
         return;
      }
      const map = mapFacade.getMapPop();
      if (!map) {
         console.warn('handleRowClick: map is not available');
         return;
      }

      // Clear any existing highlight
      if (highlightCleanupRef.current) {
         highlightCleanupRef.current();
         highlightCleanupRef.current = null;
      }

      if (property?.geoshape) {
         try {
            const geoshapeFeature = feature(property.geoshape);
            mapFacade.zoomToDataAvoidingWindows(geoshapeFeature, 25);

            // Highlight the polygon with bright green outline
            highlightCleanupRef.current = highlightPolygonOnMap(
               map,
               property.geoshape,
               property.documentID,
               5000 // 5 seconds, matching table row highlight duration
            );
         } catch (error) {
            console.error('handleRowClick: error creating feature or zooming', error);
         }
      } else {
         try {
            const pointFeature = feature({
               type: "Point",
               coordinates: [property.coordinates.lng, property.coordinates.lat]
            });
            const bufferedFeature = buffer(pointFeature, 0.5, { units: 'kilometers' });
            if (bufferedFeature) {
               mapFacade.zoomToDataAvoidingWindows(bufferedFeature, 25);

               // Highlight the buffered circle with bright green outline
               highlightCleanupRef.current = highlightPolygonOnMap(
                  map,
                  bufferedFeature.geometry,
                  property.documentID,
                  5000 // 5 seconds, matching table row highlight duration
               );
            } else {
               console.warn('handleRowClick: bufferedFeature is null');
            }
         } catch (error) {
            console.error('handleRowClick: error creating point or zooming', error);
         }
      }
   };

   const handleExportReport = async () => {
      setIsGeneratingReport(true);
      try {
         const { assumptions } = getCheckedItemsFromCurrentAssumptions(
            referenceProperties,
            areaGrouping,
            appliedYearGrouping,
            customPriceAssumptions,
            priceMode
         );

         let selectedLayerInfo: JPBReportSelectedLayer | undefined;
         if (selectedLayer !== undefined) {
            const layer = layers[selectedLayer];
            const name = (layer as any)?.layer?.label ?? (layer as any)?.layer?.name ?? String(selectedLayer);
            selectedLayerInfo = { id: selectedLayer, name };
         }

         let overviewImage: Uint8Array | undefined;
         let heatmapImage: Uint8Array | undefined;
         let overviewWithJordbundImage: Uint8Array | undefined;
         let histogramImage: Uint8Array | undefined;
         try {
            if (referenceProperties.length > 0 && mapInfoRef.current?.mapStyle) {
               // Generate overview image with styled points
               overviewImage = await renderJPBOverviewImage({
                  mapStyle: mapInfoRef.current.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });

               // Generate heatmap image
               heatmapImage = await renderJPBHeatmapImage({
                  mapStyle: mapInfoRef.current.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });

               // Generate overview image with jordbund layer
               overviewWithJordbundImage = await renderJPBOverviewWithJordbundImage({
                  mapStyle: mapInfoRef.current.mapStyle,
                  referenceProperties,
                  checkedDocumentIds: Array.from(checkedItems),
                  bufferGeometry
               });
            }

            // Generate histogram image
            if (filteredDataForHistogram.length > 0 && histogramRef.current) {
               histogramImage = await renderJPBHistogramImage(histogramRef.current);
            }
         } catch (e) {
            console.error("Failed to capture JPB images", e);
         }

         const reportData: JPBReportData = {
            yearGrouping: appliedYearGrouping,
            areaGrouping,
            averageCalculationMethod,
            assumptions,
            referenceProperties,
            checkedDocumentIds: Array.from(checkedItems),
            calculationDateFrom: appliedCalculationDateTo ? withYearsOffset(appliedCalculationDateTo, -appliedYearGrouping) : undefined,
            calculationDateTo: appliedCalculationDateTo ?? undefined,
            selectedLayer: selectedLayerInfo,
            overviewImage,
            heatmapImage,
            overviewWithJordbundImage,
            histogramImage
         };

         await handleGenerateJPBRapport(reportData);
      } finally {
         setIsGeneratingReport(false);
      }
   };


   // MARK: Computed
   const selectableLayers = useMemo(() => {
      return Object.values(layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
   }, [layers]);

   const availableYearGroupings = useMemo<YearGrouping[]>(() => {
      const allYears: YearGrouping[] = [
         YearGrouping.OneYear,
         YearGrouping.TwoYears,
         YearGrouping.ThreeYears,
         YearGrouping.FourYears,
         YearGrouping.FiveYears
      ];
      if (!dateBounds || !appliedCalculationDateTo) {
         return allYears;
      }
      const minDateMs = toStartOfDay(dateBounds.minDate).getTime();
      const valid = allYears.filter((years) => {
         const fromDate = withYearsOffset(appliedCalculationDateTo, -years);
         return toStartOfDay(fromDate).getTime() >= minDateMs;
      });
      return valid.length > 0 ? valid : [YearGrouping.OneYear];
   }, [dateBounds, appliedCalculationDateTo]);

   const minCalculationDateTo = useMemo(() => {
      if (!dateBounds) return null;
      return toStartOfDay(withYearsOffset(dateBounds.minDate, appliedYearGrouping));
   }, [dateBounds, appliedYearGrouping]);

   useEffect(() => {
      if (!availableYearGroupings.includes(appliedYearGrouping)) {
         setAppliedYearGrouping(availableYearGroupings[availableYearGroupings.length - 1]);
      }
   }, [availableYearGroupings, appliedYearGrouping]);

   const filteredDataForHistogram = useMemo(() => {
      if (referenceProperties.length === 0) {
         return [];
      }
      return referenceProperties.filter(p => checkedItems.has(p.documentID));
   }, [referenceProperties, checkedItems]);

   const tableData = useMemo(() => {
      if (hasDebugAccess) {
         return referenceProperties;
      }
      return referenceProperties.filter((property) => {
         return !JPBManager.getTransactionIssueText(
            property.transactionStatus,
            property.reasonText,
            property.reasonCode
         );
      });
   }, [hasDebugAccess, referenceProperties]);

   const tableDocumentIds = useMemo(() => {
      return new Set(tableData.map((property) => property.documentID));
   }, [tableData]);


   // MARK: Data Fetch
   useEffect(() => {
      if (!mapFacade) return;
      const abortController = new AbortController();

      async function fetchData() {
         if (!bufferGeometry) {
            setReferenceProperties([]);
            setCheckedItems(new Set());
            setLoading(false);
            return;
         }

         setLoading(true);
         setError(null);
         try {
            const polygonCoordinates = JPBManager.extractPolygonCoordinates(bufferGeometry);

            if (!polygonCoordinates) {
               setError(Localization.getText("Could not extract polygon coordinates from buffer"));
               setLoading(false);
               return;
            }

            const properties = await JPBManager.fetchReferenceProperties(
               {
                  polygonCoordinates,
                  yearGrouping: appliedYearGrouping,
                  calculationDateTo: appliedCalculationDateTo ?? undefined,
                  priceIndexSource
               },
               abortController.signal
            );
            setReferenceProperties(properties);

            const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
            setSystemPriceAssumptions({
               pricePerMeterMin: systemAssumptions.pricePerMeterMin,
               pricePerMeterMax: systemAssumptions.pricePerMeterMax
            });

            if (customPriceAssumptions === undefined) {
               setCustomPriceAssumptions({
                  pricePerMeterMin: systemAssumptions.pricePerMeterMin,
                  pricePerMeterMax: systemAssumptions.pricePerMeterMax
               });
            }

            const { checkedItems: initialCheckedItems } = getCheckedItemsFromCurrentAssumptions(
               properties,
               areaGrouping,
               appliedYearGrouping,
               customPriceAssumptions,
               priceMode
            );
            const sanitizedInitialCheckedItems = new Set(initialCheckedItems);
            properties.forEach((prop) => {
               if (JPBManager.getSelectionLockState(prop, customPriceAssumptions).isLocked) {
                  sanitizedInitialCheckedItems.delete(prop.documentID);
               }
            });
            setCheckedItems(sanitizedInitialCheckedItems);
         } catch (err: any) {
            if (abortController.signal.aborted) return;
            console.error("Got error searching: ", err);
            setError(err.message || String(err));
         } finally {
            if (!abortController.signal.aborted) {
               setLoading(false);
            }
         }
      }

      fetchData();

      return () => {
         abortController.abort();
      };
   }, [bufferGeometry, appliedYearGrouping, appliedCalculationDateTo, priceIndexSource]);

   // Update checked items when areaGrouping or yearGrouping changes
   useEffect(() => {
      if (referenceProperties.length === 0) return;
      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         appliedYearGrouping,
         customPriceAssumptions,
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   }, [areaGrouping, appliedYearGrouping, referenceProperties, customPriceAssumptions, priceMode, settingsRefreshToken]);

   // Reset hideUnselected when areaUnit or priceMode changes
   useEffect(() => {
      setHideUnselected('none');
   }, [areaUnit, priceMode]);

   // MARK: Price Handlers
   const handlePriceRangeChange = (min: number, max: number) => {
      setCustomPriceAssumptions({
         pricePerMeterMin: min,
         pricePerMeterMax: max
      });

      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         appliedYearGrouping,
         { pricePerMeterMin: min, pricePerMeterMax: max },
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   };

   const handleAreaUnitChange = (unit: AreaUnit) => {
      setAreaUnit(unit);
      localStorage.setItem("jpb.areaUnit", unit);
   };

   const handlePriceModeChange = (mode: PriceMode) => {
      setPriceMode(mode);
      localStorage.setItem("jpb.priceMode", mode);
   };

   const handleAverageCalculationMethodChange = (method: AverageCalculationMethod) => {
      setAverageCalculationMethod(method);
      localStorage.setItem("jpb.averageCalculationMethod", method);
   };

   const handlePriceIndexSourceChange = (source: JPBPriceIndexSource) => {
      const effectiveSource = normalizeAccessiblePriceIndexSource(source);
      setPriceIndexSource(effectiveSource);
      localStorage.setItem("jpb.priceIndexSource", effectiveSource);
   };

   const handleCustomAssumptionsChange = (newAssumptions: Partial<JPBAssumptions>) => {
      setCustomPriceAssumptions(prev => ({
         ...prev,
         ...newAssumptions
      }));
   };

   const handleResetToSystemAssumptions = () => {
      if (!systemPriceAssumptions) return;

      setCustomPriceAssumptions({
         pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
         pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
         excludeBuildings: false
      });

      const { checkedItems: newCheckedItems } = getCheckedItemsFromCurrentAssumptions(
         referenceProperties,
         areaGrouping,
         appliedYearGrouping,
         {
            pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
            pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
            excludeBuildings: false
         },
         priceMode
      );
      updateCheckedItems(newCheckedItems);
   };

   // MARK: Map Layers
   useEffect(() => {
      if (!mapFacade || referenceProperties.length === 0) {
         removeJPBMapLayers(mapFacade?.getMapPop());
         return;
      }

      const map = mapFacade.getMapPop();
      if (!map) return;

      const cleanup = setupJPBMapLayers(
         map,
         referenceProperties,
         checkedItemsRef.current,
         priceMode,
         areaUnit,
         {
            onFeatureClick: (documentID: string, ctrlKey: boolean) => {
               if (!ctrlKey) {
                     if (tableRef.current && tableDocumentIds.has(documentID)) {
                     // The table lives at the bottom of the modal; make sure the modal body scrolls down to it first.
                     tableScrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                     tableRef.current.scrollToRow(documentID);
                  }
               } else {
                  if (lockedDocumentIds.has(documentID)) {
                     return;
                  }
                  const featureState = map.getFeatureState({ source: SOURCE_ID, id: documentID });
                  const currentChecked = featureState?.checked === true;
                  const newChecked = !currentChecked;

                  const newCheckedItems = new Set(checkedItemsRef.current);
                  if (newChecked) {
                     newCheckedItems.add(documentID);
                  } else {
                     newCheckedItems.delete(documentID);
                  }

                  updateCheckedItems(newCheckedItems);
               }
            }
         },
         lockedDocumentIds
      );

      return cleanup;
   }, [mapFacade, referenceProperties, priceMode, areaUnit, lockedDocumentIds, mapRefreshToken, tableDocumentIds]);

   // Update feature states when checkedItems changes
   useEffect(() => {
      if (!mapFacade || referenceProperties.length === 0) return;
      const map = mapFacade.getMapPop();
      if (!map || !map.getSource(SOURCE_ID)) return;

      updateFeatureStates(map, referenceProperties, checkedItems);
   }, [mapFacade, checkedItems, referenceProperties]);

   useEffect(() => {
      if (!mapFacade || referenceProperties.length === 0) return;
      const map = mapFacade.getMapPop();
      if (!map) return;


      if (map.getLayer(LAYER_ID)) {
         map.setPaintProperty(LAYER_ID, 'circle-stroke-opacity',
            hideUnselected === 'unchecked'
               ? ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]
               : hideUnselected === 'locked'
                  ? ['case', ['in', ['get', 'documentID'], ['literal', Array.from(lockedDocumentIds)]], 0, 1]
                  : 1
         );
         map.setPaintProperty(LAYER_ID, 'circle-opacity',
            hideUnselected === 'unchecked'
               ? ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]
               : hideUnselected === 'locked'
                  ? ['case', ['in', ['get', 'documentID'], ['literal', Array.from(lockedDocumentIds)]], 0, 1]
                  : 1
         );
      }

      if (map.getLayer(LAYER_ID_POLY)) {
         map.setPaintProperty(
            LAYER_ID_POLY,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['feature-state', 'checked']], 0.5, 0]]
               : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.5]
         );
      }

      if (map.getLayer(LAYER_ID_POLY + "-outline")) {
         map.setPaintProperty(
            LAYER_ID_POLY + "-outline",
            'line-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 12.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 13.5, ['case', ['to-boolean', ['feature-state', 'checked']], 1, 0]]
               : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1]
         );
      }

      if (map.getLayer(LAYER_ID_FARMLAND)) {
         map.setPaintProperty(
            LAYER_ID_FARMLAND,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['feature-state', 'checked']], 0.6, 0]]
               : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6]
         );
      }

      if (map.getLayer(LAYER_ID_FARMLAND + "-outline")) {
         map.setPaintProperty(
            LAYER_ID_FARMLAND + "-outline",
            'line-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 12.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0, 0], 13.5, ['case', ['to-boolean', ['feature-state', 'checked']], 0.8, 0]]
               : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 0.8]
         );
      }

      if (map.getLayer(LAYER_ID_JORDBUNDSKORT) && map.getSource(SOURCE_ID_JORDBUNDSKORT)) {
         const jordbundskortSource = map.getSource(SOURCE_ID_JORDBUNDSKORT) as any;
         if (jordbundskortSource && jordbundskortSource.type === 'geojson') {
            const updatedJordbundskortGeoJson = createJordbundskortGeoJson(referenceProperties, checkedItems, lockedDocumentIds);
            jordbundskortSource.setData(updatedJordbundskortGeoJson);
         }
         map.setPaintProperty(
            LAYER_ID_JORDBUNDSKORT,
            'fill-opacity',
            hideUnselected === 'unchecked'
               ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['get', 'checked']], 0, 0], 14, ['case', ['to-boolean', ['get', 'checked']], 0.4, 0]]
               : hideUnselected === 'locked'
                  ? ["interpolate", ["linear"], ["zoom"], 13, ['case', ['to-boolean', ['get', 'locked']], 0, 0], 14, ['case', ['to-boolean', ['get', 'locked']], 0, 0.4]]
                  : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.4]
         );
      }

      const checkedIds = Array.from(checkedItems);
      const lockedIds = Array.from(lockedDocumentIds);

      const showOnlyCheckedFilter: any =
         checkedIds.length > 0 ? ['in', ['get', 'documentID'], ['literal', checkedIds]] : ['==', ['get', 'documentID'], '__none__'];

      const hideLockedFilter: any =
         lockedIds.length > 0 ? ['!', ['in', ['get', 'documentID'], ['literal', lockedIds]]] : null;

      const applyFilter = (layerId: string, baseFilter: any = null) => {
         if (!map.getLayer(layerId)) return;
         if (hideUnselected === 'none') {
            map.setFilter(layerId, baseFilter);
            return;
         }

         let activeFilter: any = null;
         if (hideUnselected === 'unchecked') {
            activeFilter = showOnlyCheckedFilter;
         } else if (hideUnselected === 'locked') {
            activeFilter = hideLockedFilter;
         }

         if (activeFilter) {
            map.setFilter(layerId, baseFilter ? ['all', baseFilter, activeFilter] : activeFilter);
         } else {
            map.setFilter(layerId, baseFilter);
         }
      };

      applyFilter(LAYER_ID);
      // Issue symbol layer should follow hide/show behavior like other point overlays.
      applyFilter(LAYER_ID_ISSUE, ['==', ['get', 'hasIssue'], true]);
      // Building layer needs to preserve its hasBuilding filter
      applyFilter(LAYER_ID_BUILDING, ['all', ['==', ['get', 'hasBuilding'], true], ['!=', ['get', 'hasIssue'], true]]);
      applyFilter(LAYER_ID_POLY);
      applyFilter(LAYER_ID_POLY + "-outline");
      applyFilter(LAYER_ID_FARMLAND);
      applyFilter(LAYER_ID_FARMLAND + "-outline");
      // Jordbundskort layer should not be filtered - it uses paint properties for visibility
   }, [mapFacade, hideUnselected, referenceProperties.length, checkedItems, lockedDocumentIds, mapRefreshToken]);

   // MARK: Mask Layer
   useEffect(() => {
      if (!mapFacade) return;

      const map = mapFacade.getMapPop();
      if (!map) return;

      if (!map.getSource(MASK_SOURCE_ID)) {
         map.addSource(MASK_SOURCE_ID, {
            type: 'geojson',
            data: {
               type: 'FeatureCollection',
               features: []
            }
         });
      }

      if (!map.getLayer(MASK_LAYER_ID)) {
         const maskLayerSpec: any = {
            id: MASK_LAYER_ID,
            type: 'fill',
            source: MASK_SOURCE_ID,
            paint: {
               'fill-color': '#000000',
               'fill-opacity': 0.15
            }
         };
         if (map.getLayer('jpb-search-area-outline')) {
            map.addLayer(maskLayerSpec, 'jpb-search-area-outline');
         } else {
            map.addLayer(maskLayerSpec);
         }
      }

      const updateMask = () => {
         if (!bufferGeometry) {
            const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
            if (source) {
               source.setData({
                  type: 'FeatureCollection',
                  features: []
               } as any);
            }
            return;
         }

         try {
            const worldBbox: [number, number, number, number] = [-180, -90, 180, 90];
            const worldPolygon = bboxPolygon(worldBbox);
            const bufferFeature = JPBManager.toCombinedSearchGeometry(bufferGeometry);
            if (!bufferFeature) {
               const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
               if (source) {
                  source.setData({
                     type: 'FeatureCollection',
                     features: []
                  } as any);
               }
               return;
            }

            const maskGeometry = difference(featureCollection([worldPolygon, bufferFeature]));

            if (maskGeometry) {
               const maskFeatureCollection = {
                  type: 'FeatureCollection',
                  features: [maskGeometry]
               };
               const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
               if (source) {
                  source.setData(maskFeatureCollection as any);
               }
            } else {
               const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
               if (source) {
                  source.setData({
                     type: 'FeatureCollection',
                     features: []
                  } as any);
               }
            }
         } catch (error) {
            console.error("Error creating mask layer:", error);
            const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
            if (source) {
               source.setData({
                  type: 'FeatureCollection',
                  features: []
               } as any);
            }
         }
      };

      updateMask();

      return () => {
         if (map.getLayer(MASK_LAYER_ID)) map.removeLayer(MASK_LAYER_ID);
         if (map.getSource(MASK_SOURCE_ID)) map.removeSource(MASK_SOURCE_ID);
      };
   }, [mapFacade, bufferGeometry]);

   // MARK: Pin Color Change
   useEffect(() => {
      if (!mapFacade || selectedLayer === undefined) {
         const map = mapFacade?.getMapPop();
         if (map) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && map.getLayer(layerId)) {
                  map.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
         return;
      }

      const layerInfo = layers[selectedLayer];
      if (!layerInfo || !layerInfo.handle) return;

      const isPointLayer = layerInfo.type === LayerType.GeoJSON_Point ||
         layerInfo.type === LayerType.GeoJSON_PointRadius;

      if (!isPointLayer) {
         const map = mapFacade?.getMapPop();
         if (map) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && map.getLayer(layerId)) {
                  map.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
         return;
      }

      const map = mapFacade.getMapPop();
      if (!map) return;

      const layerHandle = layerInfo.handle;
      const layerIds = layerHandle.getLayerIds();
      const pinPrefix = SettingsManager.getSystemSetting("pinPrefix", "mit-pin");

      layerIds.forEach(layerId => {
         const layer = map.getLayer(layerId);
         if (layer && layer.type === 'symbol') {
            if (!(layerId in originalIconImagesRef.current)) {
               const currentIconImage = map.getLayoutProperty(layerId, 'icon-image');
               originalIconImagesRef.current[layerId] = currentIconImage;
            }
            const newIconImage = `${pinPrefix}-#d6c251`;
            map.setLayoutProperty(layerId, 'icon-image', newIconImage);
         }
      });

      return () => {
         const cleanupMap = mapFacade?.getMapPop();
         if (cleanupMap) {
            Object.keys(originalIconImagesRef.current).forEach(layerId => {
               const originalIconImage = originalIconImagesRef.current[layerId];
               if (originalIconImage !== undefined && cleanupMap.getLayer(layerId)) {
                  cleanupMap.setLayoutProperty(layerId, 'icon-image', originalIconImage);
               }
            });
            originalIconImagesRef.current = {};
         }
      };
   }, [mapFacade, selectedLayer, layers]);
   // MARK: RETURN
   return (
      <AdvancedDragModal
         title={Localization.getText("Land Price Calculator")}
         window={managedWindow}
         allowUserMiniMize={true}
         allowFullscreen={true}
         onMinimizedChange={setIsWindowMinimized}
         className="JPBModule"
         // Default: dock to the right, tall window (so it doesn't start tiny and rely on content growth)
         PosDefault={{
            top: "12px",
            left: "calc(100% - 12px)",
            transform: "translateX(-100%)",
            width: "900px",

         }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}

         subHeaderContent={
            <JPBSearchArea
               selectableLayers={selectableLayers}
               onBufferGeometryChange={setBufferGeometry}
               onSelectedLayerChange={setSelectedLayer}
               propertiesCount={referenceProperties.length}
               areaUnit={areaUnit}
               onAreaUnitChange={handleAreaUnitChange}
               priceMode={priceMode}
               onPriceModeChange={handlePriceModeChange}
               averageCalculationMethod={averageCalculationMethod}
               onAverageCalculationMethodChange={handleAverageCalculationMethodChange}
               priceIndexSource={priceIndexSource}
               onPriceIndexSourceChange={handlePriceIndexSourceChange}
               onOpenPriceIndexSource={() => {
                  windowDispatch(openWindow(WindowId.JPBPriceIndexSource, {
                     source: priceIndexSource,
                     onConfirm: (source: JPBPriceIndexSource) => handlePriceIndexSourceChange(source)
                  } as JPBPriceIndexSourceWindowParams));
               }}
               onOpenTimeHorizon={() => {
                  windowDispatch(openWindow(WindowId.JPBTimeHorizon, {
                     calculationDateTo: appliedCalculationDateTo,
                     yearGrouping: appliedYearGrouping,
                     minCalculationDateTo: minCalculationDateTo ?? undefined,
                     maxCalculationDateTo: dateBounds?.maxDate,
                     availableYearGroupings,
                     onConfirm: (date, grouping) => {
                        setAppliedCalculationDateTo(date);
                        setAppliedYearGrouping(grouping);
                     }
                  }));
               }}
               onExportReport={handleExportReport}
               isGeneratingReport={isGeneratingReport}
               settingsRefreshToken={settingsRefreshToken}
               onOpenSettings={() => {
                  windowDispatch(openWindow(WindowId.UserSettingsEditor, { filter: (g: { key: string }) => g.key === "jpb" }));
               }}
            />
         }
         onClose={() => {
            windowDispatch(closeWindow(managedWindow.id));
         }}
      >
         <ADModalBody>
            {/* Loading overlay */}
            {loading && (
               <div className="jpb-window-loading-overlay">
                  <div className="jpb-window-loading-content">
                     <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
                     <p className="jpb-window-loading-text">
                        {Localization.getText("Fetching reference transactions...")}
                     </p>
                  </div>
               </div>
            )}
            <div ref={modalBodyRef} style={{ padding: "12px 8px", position: "relative", maxHeight: "calc(100vh - 130px)", overflowY: "auto" }}>

               {!bufferGeometry && (
                  <p>{Localization.getText("Choose a layer and set the buffer to search for reference transactions")}</p>
               )}

               {error && (
                  <div className="PropertyWarning">
                     <p>{Localization.getText("Error fetching data:")} {error}</p>
                  </div>
               )}

               {!loading && !error && bufferGeometry && referenceProperties.length === 0 && (
                  <p>{Localization.getText("No reference transactions found")}</p>
               )}
               {bufferGeometry && (
                  <div ref={histogramRef}>
                     <JPBHistogram
                        data={filteredDataForHistogram}
                        allData={referenceProperties}
                        currentMin={customPriceAssumptions?.pricePerMeterMin}
                        currentMax={customPriceAssumptions?.pricePerMeterMax}
                        onRangeChange={handlePriceRangeChange}
                        onResetToSystem={handleResetToSystemAssumptions}
                        areaUnit={areaUnit}
                        priceMode={priceMode}
                        averageCalculationMethod={averageCalculationMethod}
                     />
                  </div>
               )}
               {referenceProperties.length > 1 && checkedItems.size > 1 && (
                  <JPBStatistics
                     data={referenceProperties}
                     areaUnit={areaUnit}
                     priceMode={priceMode}
                     checkedItems={checkedItems}
                     yearGrouping={appliedYearGrouping}
                     calculationDateTo={appliedCalculationDateTo ?? undefined}
                     averageCalculationMethod={averageCalculationMethod}
                     priceIndexSource={priceIndexSource}
                  />
               )}
               {referenceProperties.length > 0 && (
                  <>
                     <div ref={tableScrollAnchorRef} />
                     <JPBTable
                        ref={tableRef}
                        data={tableData}
                        checkedItems={checkedItems}
                        onCheckedItemsChange={updateCheckedItems}
                        onRowClick={handleRowClick}
                        onAreaGroupingChange={setAreaGrouping}
                        areaGrouping={areaGrouping}
                        yearGrouping={appliedYearGrouping}
                        averageCalculationMethod={averageCalculationMethod}
                        calculationDateTo={appliedCalculationDateTo ?? undefined}
                        hideUnselected={hideUnselected}
                        onHideUnselectedChange={setHideUnselected}
                        customAssumptions={customPriceAssumptions}
                        onCustomAssumptionsChange={handleCustomAssumptionsChange}
                        areaUnit={areaUnit}
                        priceMode={priceMode}
                        priceIndexSource={priceIndexSource}
                        portalRoot={modalBodyRef.current}
                     />
                  </>
               )}
               {referenceProperties.length > 0 && (
                  <ProtectedFeature feature={Feature.JPBSimulator} contentsIfNoAccess={<></>}>
                     <JPBPolygonAnalyzer
                        referenceProperties={referenceProperties}
                        checkedItems={checkedItems}
                        areaUnit={areaUnit}
                        averageCalculationMethod={averageCalculationMethod}
                        customAssumptions={customPriceAssumptions}
                        settingsRefreshToken={settingsRefreshToken}
                     />
                  </ProtectedFeature>
               )}
            </div>
         </ADModalBody>
      </AdvancedDragModal>
   );
}

const MemoizedJPBWindowInner = memo(JPBWindowInner, (prev, next) =>
   prev.map === next.map && prev.layers === next.layers && prev.managedWindow === next.managedWindow
);

export function JPBWindow(props: { managedWindow: ManagedWindow }) {
   const { state: mapitState, dispatch } = useContext(MapitStateContext);
   const mapInfoRef = useRef<{ mapStyle?: any } | null>(null);
   mapInfoRef.current = mapitState.mapInfo;
   return (
      <MemoizedJPBWindowInner
         managedWindow={props.managedWindow}
         map={mapitState.map}
         layers={mapitState.layers}
         dispatch={dispatch}
         mapInfoRef={mapInfoRef}
      />
   );
}

export default JPBWindow;
